import React from 'react';
import PropTypes from 'prop-types';
import { SingleSelect } from '@els/els-react--select';
import { ButtonWithIcon } from '@els/els-react--button';
import { ValidatedTextInput } from '@els/els-react--text-input';
import uuid from 'uuid/v4';
import ExportModalContentTooltip from './ExportModalContentTooltip';
import { EXPORT_ERRORS } from '../../ExportModalConstants';

function ExportModalContentRange({ data, formData, setRange, setRangeOptions, trackAdobeAnalyticsError, signIn, errors, parentParams }) {
  const { range } = formData;
  const { context } = parentParams;

  const mappedRangeOptions = data.ranges?.map((rangeItem) => ({
    label: rangeItem.label + rangeItem.value,
    value: rangeItem.label + rangeItem.value,
  }));

  // Validate that the current range placeholder exists in available options
  const isValidRange = mappedRangeOptions.some((option) => option.value === range.placeholder);
  const defaultRange = mappedRangeOptions[0];
  const mappedRange = isValidRange ? { label: range.placeholder, value: range.placeholder } : defaultRange;

  const handleRangeSelect = ({ value }) => {
    const index = mappedRangeOptions.findIndex((rangeItem) => rangeItem.value === value);
    setRange(index);
    trackAdobeAnalyticsError();
  };

  const isBioactivityVisualization = context === 'dpitems';
  return (
    <div className="exportModal__ranges">
      <div className="exportModal__content_row">
        <div className="exportModal__content_row_label">Range:</div>
        <div className="exportModal__content_row_value">
          <SingleSelect
            id="export-modal-content-dropdown-range"
            key={`export-range-${mappedRange.value}-${uuid()}`}
            className="e2e-export-modal-content-dropdown-range rx-select-no-cursor"
            options={mappedRangeOptions}
            selectedOptions={mappedRange.value ? [mappedRange.value] : undefined}
            label="Select range"
            data-e2e="export-modal-content-range-dropdown"
            required
            onSelect={handleRangeSelect}
            size="small"
            shouldHideLabel
          />

          <div className="exportModal__ranges_messages errorMessage text-danger">
            {errors[EXPORT_ERRORS.RANGE_HIGH] && (
              <>
                {isBioactivityVisualization && (
                  <>
                    <span>{`The selected export range exceeds the limit of ${data.exportSizeLimit} substance and target results.`}</span>
                    <span>{`Please adjust your export range settings${
                      errors[EXPORT_ERRORS.RANGE_HIGH_ANONYMOUS_HINT] ? ' or sign-in/register to benefit from a higher export limit.' : '.'
                    }`}</span>
                  </>
                )}
                {!isBioactivityVisualization && (
                  <>
                    <span>{`The selected export range exceeds the limit of ${data.exportSizeLimit}.`}</span>
                    <span>{`Please adjust your export range settings${
                      errors[EXPORT_ERRORS.RANGE_HIGH_ANONYMOUS_HINT] ? ' or sign-in/register to benefit from a higher export limit.' : '.'
                    }`}</span>
                  </>
                )}
                {errors[EXPORT_ERRORS.RANGE_HIGH_ANONYMOUS_HINT] && (
                  <ButtonWithIcon
                    type="primary"
                    size="small"
                    sprite="ChevronRight"
                    iconRight
                    onClick={signIn}
                    data-e2e="export-modal-content-sign-in"
                    className="e2e-export-modal-content-sign-in">
                    Sign in
                  </ButtonWithIcon>
                )}
              </>
            )}
            {errors[EXPORT_ERRORS.RANGE_EMPTY] && <span>This range selected cannot be zero.</span>}
          </div>

          {range.selected === 2 && (
            <div className="exportModal__ranges_options">
              <div className="exportModal__ranges_options_inputs u-els-margin-top u-els-display-flex">
                <ValidatedTextInput
                  data-e2e="export-modal-content-range-from-input"
                  id="exportModal-content-range-from-input"
                  type="number"
                  name="exportModal-content-range-from-input"
                  className="e2e-exportModal-content-range-from-input"
                  label="From"
                  shouldHideLabel
                  placeholder="From"
                  value={range.from}
                  onChange={(event) => setRangeOptions('from', event.target?.value)}
                  validator="numeric"
                  onlyValidateWhenTouched
                  onBlur={trackAdobeAnalyticsError}
                />
                <ValidatedTextInput
                  data-e2e="export-modal-content-range-to-input"
                  id="exportModal-content-range-to-input"
                  type="number"
                  name="exportModal-content-range-to-input"
                  className="u-els-margin-left-1o4 e2e-exportModal-content-range-to-input"
                  label="To"
                  shouldHideLabel
                  placeholder="To"
                  value={range.to}
                  onChange={(event) => setRangeOptions('to', event.target?.value)}
                  validator="numeric"
                  onlyValidateWhenTouched
                  onBlur={trackAdobeAnalyticsError}
                />
              </div>
              {errors[EXPORT_ERRORS.RANGE_FROM_TO] && (
                <div role="alert" className="exportModal__ranges_options_message errorMessage text-danger">
                  This range is invalid.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="exportModal__content_row_tooltip">
          <ExportModalContentTooltip header="Range" text={data.exportSizeTooltip} />
        </div>
      </div>
    </div>
  );
}

ExportModalContentRange.propTypes = {
  data: PropTypes.object,
  formData: PropTypes.object,
  errors: PropTypes.object,
  parentParams: PropTypes.object,
  setRange: PropTypes.func.isRequired,
  setRangeOptions: PropTypes.func.isRequired,
  trackAdobeAnalyticsError: PropTypes.func.isRequired,
  signIn: PropTypes.func.isRequired,
};

export default ExportModalContentRange;
